import type { ClassificacaoRisco, ScorePagamento } from './types';

export const parseISO = (dateStr: string) => new Date(dateStr);
export const differenceInDays = (dateRight: Date, dateLeft: Date) => {
  const diffTime = dateRight.getTime() - dateLeft.getTime();
  return Math.floor(diffTime / (1000 * 60 * 60 * 24));
};

export const isInterno = (nome: string) => {
  const n = nome.toUpperCase();
  return n.includes('RAV ENERGIA') || n.includes('VIGOR') || n.includes('SICOOB') || n.includes('SINDICATO');
};

export const classifyRisk = (valor: number, qtd: number, saldo: number, interno: boolean): ClassificacaoRisco => {
  if (interno) return 'RISCO BAIXO';
  if (valor > 10000 && saldo > 5000) return 'RISCO ESTRATÉGICO ALTO';
  if (valor > 10000) return 'RISCO ALTO';
  if (valor > 2000 || qtd >= 3) return 'RISCO MÉDIO';
  return 'RISCO BAIXO';
};

export const classifyScore = (vencMaisAntigo: string | null): ScorePagamento => {
  if (!vencMaisAntigo) return 'A';
  const dias = differenceInDays(new Date(), parseISO(vencMaisAntigo));
  if (dias <= 15) return 'A';
  if (dias <= 30) return 'B';
  if (dias <= 60) return 'C';
  if (dias <= 90) return 'D';
  return 'E';
};

const MESES_ABREV: Record<string, string> = {
  JAN: '01', FEV: '02', MAR: '03', ABR: '04', MAI: '05', JUN: '06',
  JUL: '07', AGO: '08', SET: '09', OUT: '10', NOV: '11', DEZ: '12',
};

// mes_referencia é texto livre no banco: convive "JUN/2026" com "06/2026" e "12/2025".
// Normaliza para "yyyy-mm" (comparável como string); null se irreconhecível.
export const normalizeCompetencia = (mesRef: string | null | undefined): string | null => {
  if (!mesRef) return null;
  const m = mesRef.trim().toUpperCase();
  const porNome = m.match(/^([A-Z]{3})\/(\d{4})$/);
  if (porNome && MESES_ABREV[porNome[1]]) return `${porNome[2]}-${MESES_ABREV[porNome[1]]}`;
  const porNumero = m.match(/^(\d{1,2})\/(\d{4})$/);
  if (porNumero) return `${porNumero[2]}-${porNumero[1].padStart(2, '0')}`;
  return null;
};

/**
 * Dias decorridos desde o FIM da competência da fatura.
 *
 * Existe porque a idade do boleto mente sobre a idade da dívida. As faturas
 * migradas do Power Rev (mai–jul/2025) entraram com vencimento recarimbado
 * para jul/2026: aparecem como "vencidas há 25 dias" quando na verdade são
 * débito de mais de 10 meses. Medido em 25/08/2026: 20 faturas, R$ 21.844,38,
 * com competência de até 297 dias e vencimento de no máximo 179.
 *
 * Conta a partir do primeiro dia do mês SEGUINTE ao da competência — a fatura
 * de MAI/2025 só passa a ser cobrável quando maio termina.
 */
export const diasDesdeCompetencia = (
  mesRef: string | null | undefined,
  hoje: Date = new Date()
): number | null => {
  const comp = normalizeCompetencia(mesRef);
  if (!comp) return null;
  const [ano, mes] = comp.split('-').map(Number);
  // `mes` é 1-based e o construtor é 0-based: passar `mes` já dá o mês seguinte.
  const fimDaCompetencia = new Date(ano, mes, 1).getTime();
  const hojeSemHora = new Date(hoje.getFullYear(), hoje.getMonth(), hoje.getDate()).getTime();
  return Math.max(0, Math.floor((hojeSemHora - fimDaCompetencia) / 86400000));
};

export const formatCurrency = (value: number) => {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0);
};

export const formatPercent = (value: number) => {
  return new Intl.NumberFormat('pt-BR', { style: 'percent', minimumFractionDigits: 1 }).format((value || 0) / 100);
};

export const formatKwh = (value: number) => {
  return new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 0 }).format(value || 0) + ' kWh';
};
